import React, { useEffect, useRef } from 'react';
import H from '@here/maps-api-for-javascript';
import { useDispatch, useSelector } from 'react-redux';
import withTranslation from 'hoc/withTranslation';
import { enhanceGeoJSONFeatures, arePropsEqual } from '../NewJobMarkers/utils/markerHelpers';
import { setMarkerIcon } from '../NewJobMarkers/utils/markerFactory';
import { attachHoverEvents, attachInteractionEvents } from '../NewJobMarkers/hooks/useMarkerEvents';
import { useMarkerCreation } from '../NewJobMarkers/hooks/useMarkerCreation';
import { createMarkerPopup } from '../NewJobMarkers/utils/markerPopupUtils';
import { buildOrderClusterIcon, STOP_CLUSTER_ANCHOR } from './utils/clusterIconFactory';
import { buildOrderClusterPopupHtml } from './utils/clusterPopupBuilder';

const CLUSTER_EPS = 50;

const NewClusteredJobMarkers = ({ translations, map, geoJSON, ui, user, oAuth }) => {
  const dispatch = useDispatch();
  const solution = useSelector((state) => state.solution);
  const orders = useSelector((state) => state.orders[solution.show]);
  const display = useSelector((state) => state.mapContainer.display);
  const routingData = useSelector((state) => state.routingTour);

  const ordersRef = useRef(orders);
  const solutionRef = useRef(solution);
  const userRef = useRef(user);
  const oAuthRef = useRef(oAuth);
  const displayRef = useRef(display);
  const routingDataRef = useRef(routingData);
  const translationsRef = useRef(translations);
  const providerRef = useRef(null);
  const shouldRefitBoundsRef = useRef(false);

  useEffect(() => {
    ordersRef.current = orders;
    solutionRef.current = solution;
    userRef.current = user;
    oAuthRef.current = oAuth;
    displayRef.current = display;
    routingDataRef.current = routingData;
    translationsRef.current = translations;
  });

  const { onMarkerDragEnd } = useMarkerCreation(
    dispatch,
    ordersRef,
    solutionRef,
    userRef,
    oAuthRef,
    shouldRefitBoundsRef,
  );

  const onMarkerDragEndRef = useRef(onMarkerDragEnd);
  useEffect(() => {
    onMarkerDragEndRef.current = onMarkerDragEnd;
  });

  useEffect(() => {
    if (!map) return undefined;

    map.addEventListener(
      'drag',
      (ev) => {
        if (ev.target instanceof H.map.Marker && ev.target.offset) {
          ev.target.setGeometry(
            map.screenToGeo(
              ev.currentPointer.viewportX - ev.target.offset.x,
              ev.currentPointer.viewportY - ev.target.offset.y,
            ),
          );
        }
      },
      false,
    );

    const theme = {
      getClusterPresentation: (cluster) => {
        const dataPoints = [];
        cluster.forEachDataPoint((dp) => dataPoints.push(dp.getData().properties));
        const svgHtml = buildOrderClusterIcon(dataPoints);
        const marker = new H.map.Marker(cluster.getPosition(), {
          min: cluster.getMinZoom(),
          max: cluster.getMaxZoom(),
          volatility: true,
        });
        marker.setIcon(new H.map.Icon(svgHtml, { anchor: { x: STOP_CLUSTER_ANCHOR.x, y: STOP_CLUSTER_ANCHOR.y } }));
        marker.setData({ isCluster: true, cluster });
        createMarkerPopup(marker, map, STOP_CLUSTER_ANCHOR.y, () => {
          const pts = [];
          cluster.forEachDataPoint((dp) => pts.push(dp.getData().properties));
          return buildOrderClusterPopupHtml(pts, translationsRef.current);
        });
        marker.addEventListener('tap', () => {
          const bbox = cluster.getBoundingBox();
          if (bbox) map.getViewModel().setLookAtData({ bounds: bbox }, true);
        });
        return marker;
      },
      getNoisePresentation: (noisePoint) => {
        const feature = noisePoint.getData();
        const props = feature.properties;
        const marker = new H.map.Marker(noisePoint.getPosition(), {
          min: noisePoint.getMinZoom(),
          volatility: true,
        });
        marker.setData(props);
        setMarkerIcon(marker, props);
        attachHoverEvents(marker, ui, translationsRef.current, map, ordersRef);
        attachInteractionEvents(
          marker,
          feature,
          dispatch,
          ordersRef,
          solutionRef.current,
          onMarkerDragEndRef.current,
          map,
        );
        return marker;
      },
    };

    const provider = new window.H.clustering.Provider([], {
      clusteringOptions: { eps: CLUSTER_EPS, minWeight: 2 },
      theme,
    });
    const clusterLayer = new H.map.layer.ObjectLayer(provider);
    map.addLayer(clusterLayer);
    providerRef.current = provider;

    return () => {
      map.removeLayer(clusterLayer);
      providerRef.current = null;
    };
  }, [map, dispatch, ui]);

  useEffect(() => {
    if (!providerRef.current) return;

    if (!display?.showProblem || !geoJSON?.geo?.features || geoJSON.geo.features.length === 0) {
      providerRef.current.setDataPoints([]);
      return;
    }

    const enhancedGeoJSON = enhanceGeoJSONFeatures(
      geoJSON,
      ordersRef.current,
      displayRef.current,
      userRef.current,
      routingDataRef.current,
    );

    if (!enhancedGeoJSON?.geo?.features || enhancedGeoJSON.geo.features.length === 0) {
      providerRef.current.setDataPoints([]);
      return;
    }

    const dataPoints = enhancedGeoJSON.geo.features
      .filter((f) => f.geometry.type === 'Point')
      .map((f) => {
        const [lng, lat] = f.geometry.coordinates;
        return new window.H.clustering.DataPoint(lat, lng, 1, f);
      });

    providerRef.current.setDataPoints(dataPoints);
  }, [geoJSON, display?.showProblem]);

  return null;
};

const MemoizedNewClusteredJobMarkers = React.memo(NewClusteredJobMarkers, arePropsEqual);

export default withTranslation(MemoizedNewClusteredJobMarkers);

